import { Link } from "#src/components/Router.tsx";
import { useStore } from "#src/stores/Store.tsx";
import colors from "#src/utils/colors.ts";
import tw from "#src/utils/tw.ts";
import { ReactNode } from "react";

interface Props {
  className?: string;
  children: ReactNode;
  onClick?: (e) => void;
  disabled?: boolean;
  round?: boolean;
  type?: "reset" | "submit" | "button";
  small?: boolean;
  xSmall?: boolean;
  dashed?: boolean;
  bgColor?: string;
  borderColor?: string;
  id?: string;
  isPreview?: boolean;
  textColor?: string;
  to?: string;
  href?: string;
  target?: string;
  style?: React.CSSProperties;
}

export function BorderButton(props: Props) {
  const store = useStore();

  let backgroundColor = colors.bgGray;
  let backgroundActiveColor = colors.borderGray;
  let borderColor = colors.borderGray;
  let borderActiveColor = colors.darkGray;
  let color = colors.dark;

  if (!store) {
    backgroundColor = colors.main;
    backgroundActiveColor = colors.mainDark;
    borderColor = colors.mainDark;
    borderActiveColor = colors.mainDarker;
  }

  if ((store?.isAdminArea || store?.isSuperAdminArea) && !props.isPreview) {
    if (props.type === "submit") {
      backgroundColor = colors.main;
      backgroundActiveColor = colors.mainDark;
      borderColor = colors.mainDark;
      borderActiveColor = colors.mainDarker;
    }
  } else if (store?.theme) {
    color = store?.theme.orgOrgColor;

    if (props.type === "submit") {
      backgroundColor = store?.theme.orgOrgColor;
      backgroundActiveColor = `color-mix(in srgb, ${store.theme.orgOrgColor}, black 10%)`;
      borderColor = `color-mix(in srgb, ${store.theme.orgOrgColor}, black 20%)`;
      borderActiveColor = `color-mix(in srgb, ${store.theme.orgOrgColor}, black 40%)`;
      color = "#fff";
    }
  }

  if (props.bgColor) {
    backgroundColor = props.bgColor;
    backgroundActiveColor = `color-mix(in srgb, ${props.bgColor}, black 10%)`;
  }

  if (props.borderColor) {
    borderColor = props.borderColor;
    borderActiveColor = `color-mix(in srgb, ${props.borderColor}, black 30%)`;
  }

  if (props.textColor) {
    color = props.textColor;
  }

  if (props.className?.includes("border-organization")) {
    borderColor = undefined;
  }

  if (props.className?.includes("text-")) {
    color = undefined;
  }

  const classes = tw(
    props.dashed ? "border-dashed border-b" : "border-b-3",
    props.dashed && !props.disabled && "hover:border-empty",
    !props.dashed && !props.disabled && "active:border-b active:border-t-3",
    "align-bottom",
    "border-t border-l border-r",
    "rounded-full",
    !props.round && !props.xSmall && "px-5",
    !props.round && props.xSmall && "px-3",
    "font-bold",
    "transition-all",
    props.small ? "h-9" : "h-12",
    props.xSmall && "h-8",
    !props.round && "truncate",
    props.disabled && "opacity-60",
    props.round ? (props.small ? "w-9" : props.xSmall ? "w-8" : "w-12") : "",
    "bg-[var(--borderButton-backgroundColor)]",
    "border-[var(--borderButton-borderColor)]",
    "text-[var(--borderButton-color)]",
    "active:bg-[var(--borderButton-backgroundActiveColor)]",
    !props.disabled && "active:border-[var(--borderButton-borderActiveColor)]"
  );

  const style = {
    "--borderButton-backgroundColor": backgroundColor,
    "--borderButton-borderColor": borderColor,
    "--borderButton-color": color,
    "--borderButton-backgroundActiveColor": backgroundActiveColor,
    "--borderButton-borderActiveColor": borderActiveColor,
  } as React.CSSProperties & {
    "--borderButton-backgroundColor": string;
    "--borderButton-borderColor": string;
    "--borderButton-color": string;
    "--borderButton-backgroundActiveColor": string;
    "--borderButton-borderActiveColor": string;
  };

  if (props.to) {
    return (
      <Link
        to={props.disabled ? undefined : props.to}
        className={tw(
          classes,
          "inline-block",
          "cursor-pointer",
          "active:border-t-3 active:border-b",
          "text-center",
          "inline-flex items-center justify-center gap-2",
          props.className
        )}
        style={{
          ...style,
          ...props.style,
        }}
        onClick={props.disabled ? undefined : props.onClick}
        id={props.id}
      >
        {props.children}
      </Link>
    );
  }

  if (props.href) {
    return (
      <a
        href={props.href}
        className={tw(
          classes,
          "inline-block",
          "cursor-pointer",
          "active:border-t-3 active:border-b",
          "text-center",
          "inline-flex items-center justify-center",
          props.className
        )}
        style={{
          ...style,
          ...props.style,
        }}
        target={props.target}
        onClick={props.onClick}
      >
        {props.children}
      </a>
    );
  }

  return (
    <button
      onClick={props.disabled ? undefined : props.onClick}
      id={props.id}
      className={tw(
        classes,
        !props.disabled && (props.onClick || props.type === "submit")
          ? "cursor-pointer"
          : "cursor-default",
        props.className
      )}
      style={{
        ...style,
        ...props.style,
      }}
      disabled={props.disabled}
      type={props.type || "button"}
      data-test={props["data-test"]}
    >
      {props.children}
    </button>
  );
}
